import { useEffect, useId, useRef, type RefObject } from "react";
import { Alert, Badge, Box, Button, Group, Stack, Text, ThemeIcon, Title, UnstyledButton } from "@mantine/core";
import { Check, ChevronLeft, ChevronRight, GraduationCap, PartyPopper, RotateCcw, Sparkles } from "lucide-react";
import { GuideChecklist, Markdown } from "@nodus/ui";
import type { GuideDetail, GuideStep } from "@/lib/api/guides";
import { toChecklistStep } from "@/lib/guides";
import {
  completion,
  nextLabel,
  positionLabel,
  reviewStartIndex,
  stepDoneToggle,
  NO_VISIT,
  type PracticeNotice,
  type StepVisit,
  type StepperScreen,
} from "@/lib/guide-stepper";
import { GuideStepBody, GuideStepDoneSummary } from "./guide-step";
import { blockDoneMode } from "./blocks/done";
import { PRACTICE_TITLE } from "./blocks/practice";
import { StepIcon } from "./step-icon";

/** More dots than this don't fit a phone row; the "Step N of M" text stays. */
const MAX_DOTS = 12;

export interface GuideStepperViewProps {
  guide: Pick<GuideDetail, "intro" | "status"> & { key?: string };
  /** The steps this viewer sees (with a tick being saved already applied). */
  steps: GuideStep[];
  screens: StepperScreen[];
  index: number;
  onGo: (index: number) => void;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  /** Mark an `info` step as not done again. */
  onUntick: (step: GuideStep) => void;
  /** What finishing a step's block does (tick it, or recheck an auto step). */
  onBlockCompleted: (step: GuideStep) => (() => void) | undefined;
  /** The step whose tick is being saved. */
  pendingKey: string | null;
  /** Done steps whose form shows anyway, while the member is on them (see
   *  `StepVisit`). Never saved. */
  visit?: StepVisit;
  /** "Change this" on a collapsed done step. */
  onReveal?: (step: GuideStep) => void;
  /** "Hide" on a done step opened with Change this. */
  onHide?: (step: GuideStep) => void;
  /** "Go through it again" on the completion screen (review only, no writes). */
  onGoThroughAgain?: () => void;
  /** After a practice run: what to say, on the screen the guide moved to. */
  practiced?: PracticeNotice | null;
}

function stepHint(step: GuideStep): string {
  if (step.done) return "This step is done.";
  if (step.behaviour === "action") return "This step is done when you finish it above. You can also press Next and do it later.";
  if (step.behaviour === "auto") return "We check this step for you. You can press Next and do it later.";
  // Don't say it twice: the step's own text may already tell them.
  if (/press next/i.test(step.body)) return "";
  return "Press Next when you have done this step.";
}

function ReopenedNotice() {
  return (
    <Alert color="orange" variant="light" icon={<Sparkles size={18} />}>
      There are new steps since you finished this guide.
    </Alert>
  );
}

/** "This was a practice run": nothing happened for real (`blocks/practice.ts`). */
function PracticeAlert({ text }: { text: string }) {
  return (
    <Alert color="teal" variant="light" icon={<GraduationCap size={18} />} title={PRACTICE_TITLE} role="status">
      {text}
    </Alert>
  );
}

function ScreenHeading({ headingRef, children }: { headingRef: RefObject<HTMLHeadingElement | null>; children: React.ReactNode }) {
  return (
    <Title order={3} ref={headingRef} tabIndex={-1} data-autofocus style={{ outline: "none" }}>
      {children}
    </Title>
  );
}

/**
 * One screen of the guide stepper (#436) plus its footer: the intro, a step
 * (title, content, what ticks it) or the end screen, then Back · progress
 * dots · Next. The footer sticks to the bottom of the modal (and of the
 * phone screen in the full-screen modal). Focus moves to the screen's heading
 * whenever the screen changes.
 */
export function GuideStepperView(props: GuideStepperViewProps) {
  const { guide, steps, screens, index, onGo, onNext, onBack, onClose, onUntick, onBlockCompleted, pendingKey } = props;
  const screen = screens[index];
  const { visit = NO_VISIT, onReveal, onHide, practiced } = props;
  const goThroughAgain = props.onGoThroughAgain ?? (() => onGo(reviewStartIndex(screens)));
  const bodyId = useId();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const changeRef = useRef<HTMLButtonElement | null>(null);
  // After Change this, focus moves into the form it shows; after Hide, back to
  // Change this. Set by the click, used once the screen has re-rendered.
  const focusAfter = useRef<"body" | "change" | null>(null);
  const toggleState = screen?.kind === "step" ? stepDoneToggle(screen.step, blockDoneMode, visit) : null;
  useEffect(() => {
    const target = focusAfter.current === "body" ? bodyRef.current : focusAfter.current === "change" ? changeRef.current : null;
    focusAfter.current = null;
    target?.focus();
  }, [toggleState]);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const screenId = screen?.id;
  // Focus the screen's heading — on every screen change, and on the first
  // screen too (the modal's focus trap may have focused the header while the
  // guide was loading).
  useEffect(() => {
    const frame = requestAnimationFrame(() => headingRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [screenId]);

  const stepScreens = screens
    .map((s, i) => [s, i] as const)
    .filter((pair): pair is readonly [Extract<StepperScreen, { kind: "step" }>, number] => pair[0].kind === "step");

  const practiceAlert = practiced && screen && practiced.at === screen.id ? <PracticeAlert text={practiced.text} /> : null;
  let content: React.ReactNode = null;
  if (screen?.kind === "intro") {
    content = (
      <Stack gap="md">
        <ScreenHeading headingRef={headingRef}>Before you start</ScreenHeading>
        {guide.status === "reopened" && <ReopenedNotice />}
        <Markdown size="sm">{guide.intro}</Markdown>
        <Text fz="sm" c="dimmed">
          {steps.length === 1 ? "This guide has 1 step." : `This guide has ${steps.length} steps.`}
        </Text>
      </Stack>
    );
  } else if (screen?.kind === "step") {
    const { step } = screen;
    const done = step.done;
    // A reopened guide opens on its first open step (built-in guides have no
    // intro): say why it is open again there too.
    const showReopened = guide.status === "reopened" && completion(steps).firstOpen === screen.id;
    const toggle = toggleState;
    const collapsed = toggle === "change";
    const hint = collapsed ? "" : stepHint(step);
    content = (
      <Stack gap="sm">
        {practiceAlert}
        {showReopened && <ReopenedNotice />}
        {(step.audience === "admins" || (step.important && !done) || done) && (
          <Group gap="xs">
            {done && (
              <Badge color="teal" variant="light" leftSection={<Check size={12} />}>
                Done
              </Badge>
            )}
            {step.audience === "admins" && (
              <Badge color="violet" variant="light">
                Company step
              </Badge>
            )}
            {step.important && !done && (
              <Badge color="orange" variant="light" leftSection={<Sparkles size={10} />}>
                Important
              </Badge>
            )}
          </Group>
        )}
        <Group gap="sm" wrap="nowrap" align="center">
          <StepIcon name={step.icon} />
          <ScreenHeading headingRef={headingRef}>{step.title}</ScreenHeading>
        </Group>
        {step.audience === "admins" && done && step.done_by && (
          <Text fz="xs" c="dimmed">
            Done by {step.done_by}
          </Text>
        )}
        {collapsed && (
          <GuideStepDoneSummary
            step={step}
            controls={bodyId}
            buttonRef={changeRef}
            onChange={() => {
              focusAfter.current = "body";
              onReveal?.(step);
            }}
          />
        )}
        {/* Always in the page (empty while collapsed), so Change this / Hide can point at it. */}
        <Box id={bodyId} ref={bodyRef} tabIndex={-1} style={{ outline: "none" }}>
          {!collapsed && (
            <GuideStepBody
              step={step}
              onCompleted={onBlockCompleted(step)}
              guideKey={guide.key}
              stepNumber={(key) => stepScreens.find(([s]) => s.step.key === key)?.[0].number ?? null}
            />
          )}
        </Box>
        <Group gap="xs" wrap="wrap">
          {hint && (
            <Text fz="sm" c="dimmed">
              {hint}
            </Text>
          )}
          {toggle === "hide" && (
            <Button
              variant="subtle"
              size="compact-sm"
              aria-expanded
              aria-controls={bodyId}
              onClick={() => {
                focusAfter.current = "change";
                onHide?.(step);
              }}
            >
              Hide
            </Button>
          )}
          {done && step.behaviour === "info" && (
            <Button variant="subtle" size="compact-sm" onClick={() => onUntick(step)} loading={pendingKey === step.key}>
              Mark as not done
            </Button>
          )}
        </Group>
      </Stack>
    );
  } else if (screen?.kind === "done") {
    const result = completion(steps);
    const empty = steps.length === 0;
    const firstOpen = result.firstOpen ? screens.findIndex((s) => s.id === result.firstOpen) : -1;
    content = (
      <Stack gap="md">
        {practiceAlert}
        <Stack gap="xs" align="center" ta="center" py="md">
          {!empty && (
            <ThemeIcon size={56} radius="xl" variant="light" color={result.complete ? "teal" : "blue"}>
              {result.complete ? <PartyPopper size={28} /> : <Check size={28} />}
            </ThemeIcon>
          )}
          <ScreenHeading headingRef={headingRef}>
            {empty ? "No steps for you" : result.complete ? "Well done — you finished this guide 🎉" : "You are at the end"}
          </ScreenHeading>
          <Text fz="sm" c="dimmed">
            {empty
              ? "This guide has no steps for you."
              : result.complete
              ? "All the steps are done."
              : result.remaining === 1
                ? "1 step is not done yet."
                : `${result.remaining} steps are not done yet.`}
          </Text>
          {/* Review only: the ticks stay, nothing is saved (owner ruling). */}
          {result.complete && (
            <Button variant="light" leftSection={<RotateCcw size={16} />} onClick={goThroughAgain}>
              Go through it again
            </Button>
          )}
          {firstOpen >= 0 && (
            <Button variant="light" onClick={() => onGo(firstOpen)}>
              Go to the first step that is not done
            </Button>
          )}
        </Stack>
        {!empty && <GuideChecklist steps={steps.map(toChecklistStep)} />}
      </Stack>
    );
  }

  const label = nextLabel(screens, index);
  const current = screen?.kind === "step" ? screen.id : null;

  return (
    <Stack gap={0} flex={1} mih={0} justify="space-between">
      <Box pb="md">{content}</Box>
      <Box
        component="footer"
        pos="sticky"
        bottom={0}
        pt="sm"
        pb="xs"
        bg="var(--mantine-color-body)"
        style={{ borderTop: "1px solid var(--mantine-color-default-border)", zIndex: 1 }}
      >
        {stepScreens.length > 1 && stepScreens.length <= MAX_DOTS && (
          <Group gap={2} justify="center" mb={4} wrap="wrap" role="group" aria-label="Steps">
            {stepScreens.map(([s, i]) => {
              const isCurrent = s.id === current;
              return (
                // A 24 px tap target (WCAG 2.5.8) around the small dot.
                <UnstyledButton
                  key={s.id}
                  onClick={() => onGo(i)}
                  aria-label={`Step ${s.number}: ${s.step.title}${s.step.done ? " (done)" : ""}`}
                  aria-current={isCurrent ? "step" : undefined}
                  miw={24}
                  h={24}
                  px={4}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 999 }}
                >
                  <Box
                    component="span"
                    w={isCurrent ? 22 : 10}
                    h={10}
                    style={{
                      display: "block",
                      borderRadius: 999,
                      transition: "width 150ms ease",
                      background: isCurrent
                        ? "var(--mantine-primary-color-filled)"
                        : s.step.done
                          ? "var(--mantine-color-teal-5)"
                          : "var(--mantine-color-default-border)",
                    }}
                  />
                </UnstyledButton>
              );
            })}
          </Group>
        )}
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Button
            variant="default"
            leftSection={<ChevronLeft size={16} />}
            onClick={onBack}
            disabled={index === 0}
            aria-label="Back"
          >
            Back
          </Button>
          <Text fz="sm" c="dimmed" ta="center" aria-live="polite" miw={0} truncate>
            {positionLabel(screen)}
          </Text>
          <Button
            onClick={label === "Close" ? onClose : onNext}
            rightSection={label === "Next" ? <ChevronRight size={16} /> : undefined}
            color={label === "Done" ? "teal" : undefined}
          >
            {label}
          </Button>
        </Group>
      </Box>
    </Stack>
  );
}
